import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Phone } from 'lucide-react';
const faqs = [{
  question: 'What areas do you deliver to?',
  answer: 'We deliver throughout Tacoma, Puyallup, Federal Way, Auburn, Kent, Bonney Lake, and surrounding South King County areas.'
}, {
  question: 'What is the minimum order?',
  answer: 'Our minimum order is 5 cubic yards for all materials.'
}, {
  question: 'Do you offer same-day delivery?',
  answer: 'Yes! We offer same-day delivery for orders placed before 10 AM, subject to availability.'
}, {
  question: 'How do I know how much material I need?',
  answer: "Use our Material Calculator above, or give us a call and we'll help you estimate based on your project dimensions."
}, {
  question: 'What forms of payment do you accept?',
  answer: 'We accept cash, check, and all major credit cards. Payment is due upon delivery.'
}, {
  question: 'Can I pick up materials instead of delivery?',
  answer: 'We are a delivery-only service at this time. All materials are delivered directly to your location by our professional drivers.'
}, {
  question: 'Do you spread the gravel after delivery?',
  answer: 'It depends on the situation. In some cases we can spread the material depending on access, terrain, and the type of project. Give us a call to discuss your specific setup, and we will let you know what is possible.'
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-16 sm:py-24 bg-stone-200 dark:bg-[#2c2c2c] transition-colors duration-300 scroll-mt-32" id="faq" data-id="element-378">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mb-12 sm:mb-20 text-center" data-id="element-379">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} data-id="element-380">
          <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-6" data-id="element-381">
            Da FAQs
          </h2>
          <p className="text-lg sm:text-xl text-stone-700 dark:text-stone-200 max-w-2xl mx-auto" data-id="element-382">
            Quick answers about delivery, materials & pricing. If your question
            isn't listed below, just give us a call.
          </p>
        </motion.div>
      </div>

      {/* FAQ List - Full Width */}
      <div className="w-full border-t border-stone-300 dark:border-[#555555]" data-id="element-383">
        {faqs.map((faq, index) => {
        const isOpen = openIndex === index;
        return <motion.div key={index} initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05,
          duration: 0.3
        }} className={`w-full border-b border-stone-300 dark:border-[#555555] transition-colors duration-300 ${isOpen ? 'bg-[#036b3b]' : 'bg-transparent'}`} data-id="element-384">
              <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-385">
                <button onClick={() => toggleFAQ(index)} className="w-full flex items-start justify-between gap-6 py-8 sm:py-12 text-left group" data-id="element-386">
                  <span className={`font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight transition-colors duration-300 ${isOpen ? 'text-white' : 'text-stone-900 dark:text-white group-hover:text-[#05894d] dark:group-hover:text-[#06a05a]'}`} data-id="element-387">
                    {faq.question}
                  </span>
                  <motion.span animate={{
                rotate: isOpen ? 45 : 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className={`shrink-0 mt-1 sm:mt-2 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-388">
                    <Plus className="w-8 h-8 sm:w-12 sm:h-12 stroke-[1]" data-id="element-389" />
                  </motion.span>
                </button>

                <AnimatePresence data-id="element-390">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-391">
                      <p className="pb-12 sm:pb-16 text-lg sm:text-xl md:text-2xl text-white/95 leading-relaxed max-w-4xl font-medium" data-id="element-392">
                        {faq.answer}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Still Have Questions */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-16 sm:mt-24" data-id="element-393">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 p-8 sm:p-12 bg-stone-100 dark:bg-[#1f1f1f] rounded-3xl shadow-sm border border-stone-300 dark:border-[#444]" data-id="element-394">
          <div data-id="element-395">
            <h3 className="text-2xl sm:text-3xl text-stone-900 dark:text-white font-heading font-extrabold mb-2" data-id="element-396">
              Still have questions?
            </h3>
            <p className="text-lg text-stone-600 dark:text-stone-400" data-id="element-397">
              We're happy to help. Give us a call to discuss your project.
            </p>
          </div>
          <a href="tel:206-619-3742" className="flex items-center justify-center px-8 py-4 bg-[#05894d] hover:bg-[#047a42] text-white rounded-full font-bold text-lg transition-colors group shrink-0" data-id="element-398">
            <Phone className="w-5 h-5 mr-3" data-id="element-399" />
            <span data-id="element-400">(206) 619-3742</span>
          </a>
        </div>
      </div>
    </section>;
}